import { insightsIntl } from "../strings/intl";
import type { DateRangeOption } from "@wayflyer/flyui";
import { DateRangeOptions } from "@wayflyer/flyui";
import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isLeapYear from "dayjs/plugin/isLeapYear";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
// eslint-disable-next-line no-restricted-imports -- chart helpers still use moment-range until PFF-576
import moment, { type Moment } from "moment";
import type { TimeRangeAndTempResProps as SharedTimeRangeAndTempResProps, TimeResState } from "shared-utils";
import { useDateAndTempGeneric, setQueryParameters } from "shared-utils";

import type { CustomTimeRange, TemporalResolution } from "./types";

export type InsightsTimeRangeAndTempResProps<D extends string> = Omit<
  SharedTimeRangeAndTempResProps<D>,
  "temporalResolution"
> & {
  temporalResolution: TemporalResolution;
};

dayjs.extend(customParseFormat);
dayjs.extend(isLeapYear);
dayjs.extend(quarterOfYear);

/** moment-range and overview chart helpers still require Moment instances */
const toMoment = (date: Dayjs): Moment => moment(date.valueOf());

const toMomentRange = (start: Dayjs, end: Dayjs): CustomTimeRange => ({
  start: toMoment(start),
  end: toMoment(end),
});

export const timeRangeLabels: { [key in DateRangeOptions]?: string } = {
  LAST_7_DAYS: insightsIntl.formatMessage({ id: "last 7 days label", defaultMessage: "Last 7 days" }),
  LAST_30_DAYS: insightsIntl.formatMessage({ id: "last 30 days label", defaultMessage: "Last 30 days" }),
  LAST_60_DAYS: insightsIntl.formatMessage({ id: "last 60 days label", defaultMessage: "Last 60 days" }),
  LAST_90_DAYS: insightsIntl.formatMessage({ id: "last 90 days label", defaultMessage: "Last 90 days" }),
  LAST_365_DAYS: insightsIntl.formatMessage({ id: "last 365 days label", defaultMessage: "Last 12 months" }),
  MONTH_TO_DATE: insightsIntl.formatMessage({ id: "month to date label", defaultMessage: "Month to date" }),
  QUARTER_TO_DATE: insightsIntl.formatMessage({ id: "quarter to date label", defaultMessage: "Quarter to date" }),
  YEAR_TO_DATE: insightsIntl.formatMessage({ id: "year to date label", defaultMessage: "Year to date" }),
  CUSTOM: insightsIntl.formatMessage({ id: "custom date range label", defaultMessage: "Custom" }),
};

export const getDaysOffset = (option: DateRangeOptions): number => {
  const now = dayjs();
  switch (option) {
    case "LAST_7_DAYS":
      return 7;
    case "LAST_30_DAYS":
      return 30;
    case "LAST_60_DAYS":
      return 60;
    case "LAST_90_DAYS":
      return 90;
    case "LAST_365_DAYS": {
      // Match legacy Moment boundaries: month 2/day 28 and month 3/day 1 (0-indexed months).
      const thisYearAndAfter = now.isLeapYear() && now.isAfter(dayjs(`${now.year()}-03-28`, "YYYY-MM-DD"));
      const lastYearAndBefore =
        dayjs(`${now.year() - 1}-01-01`, "YYYY-MM-DD").isLeapYear() &&
        now.isBefore(dayjs(`${now.year()}-04-01`, "YYYY-MM-DD"));
      return thisYearAndAfter || lastYearAndBefore ? 366 : 365;
    }
    case "MONTH_TO_DATE":
      return now.date() - 1;
    case "QUARTER_TO_DATE": {
      const startOfQuarter = now.startOf("quarter");
      return dayjs().diff(startOfQuarter, "day");
    }
    case "YEAR_TO_DATE": {
      const startOfYear = now.startOf("year");
      return dayjs().diff(startOfYear, "day");
    }
  }
  return 0;
};

export const dateRangePresets: DateRangeOption[] = Object.keys(timeRangeLabels)
  .filter((key) => key !== "CUSTOM")
  .map((preset) => {
    return {
      text: timeRangeLabels[preset],
      id: preset as DateRangeOptions,
      startDate: dayjs()
        .subtract(getDaysOffset(preset as DateRangeOptions), "day")
        .startOf("day")
        .format("YYYY-MM-DD"),
      endDate: dayjs().subtract(1, "day").startOf("day").format("YYYY-MM-DD"),
    };
  });

export const convertToTimeRange = (
  selectedDateRange: DateRangeOptions,
  selectedStartDate?: string,
  selectedEndDate?: string
): CustomTimeRange => {
  if (selectedDateRange === "CUSTOM") {
    return toMomentRange(dayjs(selectedStartDate, "YYYY-MM-DD"), dayjs(selectedEndDate, "YYYY-MM-DD"));
  } else {
    switch (selectedDateRange) {
      case "MONTH_TO_DATE":
        return toMomentRange(dayjs().startOf("month"), dayjs().endOf("month"));
      case "YEAR_TO_DATE":
        return toMomentRange(dayjs().startOf("year"), dayjs().endOf("year"));
      case "QUARTER_TO_DATE":
        return toMomentRange(dayjs().startOf("quarter"), dayjs().endOf("quarter"));
    }
    return toMomentRange(
      dayjs().subtract(getDaysOffset(selectedDateRange), "day").startOf("day"),
      dayjs().subtract(1, "day").startOf("day")
    );
  }
};

const DEFAULT_DATE_RANGE = "LAST_365_DAYS";

export const useDateAndTemp = (
  dateChangeCallback: (c: CustomTimeRange) => void = () => {},
  temporalResolutionChangeCallback: (t: TimeResState) => void = () => {}
): InsightsTimeRangeAndTempResProps<DateRangeOptions> => {
  const result = useDateAndTempGeneric(
    setQueryParameters,
    convertToTimeRange,
    dateChangeCallback,
    temporalResolutionChangeCallback,
    DEFAULT_DATE_RANGE,
    timeRangeLabels
  );
  return {
    ...result,
    temporalResolution: result.temporalResolution as TemporalResolution,
  };
};
